import * as XLSX from 'xlsx';
import { ymdToBr } from '../../relatorios/utils/relatoriosPeriodDateUtils';
import { getTechnicianDisplayStatus, TECH_STATUS_TYPES } from './monitoramentoStatusUtils';

const HOUR_START = 7;

const HEADER_STYLE = {
  font: { bold: true, color: { rgb: 'FFFFFFFF' }, sz: 11 },
  fill: { fgColor: { rgb: 'FF7C5CFC' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: 'FFDCE1E8' } },
    bottom: { style: 'thin', color: { rgb: 'FFDCE1E8' } },
    left: { style: 'thin', color: { rgb: 'FFDCE1E8' } },
    right: { style: 'thin', color: { rgb: 'FFDCE1E8' } }
  }
};

const TITLE_STYLE = {
  font: { bold: true, sz: 14, color: { rgb: 'FF7C5CFC' } },
  alignment: { vertical: 'center' }
};

const SECTION_STYLE = {
  font: { bold: true, sz: 10, color: { rgb: 'FF1A2230' } },
  fill: { fgColor: { rgb: 'FFEEF1F4' } },
  alignment: { vertical: 'center' }
};

const CELL_BORDER = {
  top: { style: 'thin', color: { rgb: 'FFE7EBEF' } },
  bottom: { style: 'thin', color: { rgb: 'FFE7EBEF' } },
  left: { style: 'thin', color: { rgb: 'FFE7EBEF' } },
  right: { style: 'thin', color: { rgb: 'FFE7EBEF' } }
};

function buildFilename() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `monitoramento-externo-${stamp}.xlsx`;
}

function dash(val) {
  if (val == null || val === '') return '—';
  return String(val);
}

function formatOsList(items, tempoKey) {
  if (!items?.length) return '—';
  return items
    .map((item) => {
      const os = typeof item === 'string' ? item : item?.os;
      if (!os) return null;
      const tempo = typeof item === 'object' && item != null ? item[tempoKey] : null;
      return tempo ? `${os} (${tempo})` : String(os);
    })
    .filter(Boolean)
    .join('; ');
}

function timelineMinToClock(min) {
  if (min == null || !Number.isFinite(min)) return '—';
  const total = HOUR_START * 60 + min;
  const h = Math.floor(total / 60);
  const m = Math.round(total % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTimeFromIso(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Hora civil America/Sao_Paulo a partir de instante UTC (almoço). */
function formatLunchTimeFromIso(iso) {
  if (!iso) return '—';
  let s = String(iso).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{1,2}:\d{2}/.test(s) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(s)) {
    s = `${s}Z`;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(d);
}

function formatMinutesAsHhMm(mins) {
  if (!Number.isFinite(mins)) return '—';
  const rounded = Math.max(0, Math.round(mins));
  const h = Math.floor(rounded / 60);
  const m = rounded % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatDurationFromMin(start, end) {
  if (start == null || end == null) return '—';
  const mins = Math.max(0, Math.round(end - start));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatActivityType(type) {
  if (type === 'concluida') return 'OS concluída';
  if (type === 'deslocamento_trajeto') return 'Deslocamento';
  if (type === 'almoco') return 'Almoço';
  return dash(type);
}

function isFilled(val) {
  if (val == null) return false;
  const s = String(val).trim();
  return s !== '' && s.toLowerCase() !== 'null';
}

function applyHeaderRow(ws, rowIndex, colCount) {
  for (let c = 0; c < colCount; c += 1) {
    const addr = XLSX.utils.encode_cell({ r: rowIndex, c });
    if (!ws[addr]) continue;
    ws[addr].s = { ...HEADER_STYLE };
  }
}

function applyDataStyles(ws, startRow, endRow, colCount) {
  for (let r = startRow; r <= endRow; r += 1) {
    const zebra = r % 2 === 0;
    for (let c = 0; c < colCount; c += 1) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) continue;
      ws[addr].s = {
        alignment: { vertical: 'center', wrapText: c <= 2 },
        border: CELL_BORDER,
        ...(zebra ? { fill: { fgColor: { rgb: 'FFF8FAFC' } } } : {})
      };
    }
  }
}

function buildResumoMatrix(data) {
  const { exportMeta, kpis, tempoMedioGeral, tempoMedioDeslocamentoGeral, horaAtual, selectedDate, isLive } = data;
  const filters = exportMeta?.filters || [];
  const matrix = [
    ['Monitoramento — Equipe Externa'],
    [],
    ['Filtros aplicados'],
    ...filters.map((f) => [f.label, f.value]),
    [],
    ['Indicadores'],
    ['Métrica', 'Valor'],
    ['Data de referência', selectedDate ? ymdToBr(selectedDate) : '—'],
    ['Atualização', isLive ? 'Tempo real' : 'Histórico'],
    ['Técnicos ativos', `${kpis?.ativos ?? 0}/${kpis?.total ?? 0}`],
    ['OS concluídas / roteiro', `${kpis?.osConcluidas ?? 0}/${kpis?.meta ?? 0} (${kpis?.metaPct ?? 0}%)`],
    ['Retornos do dia', String(kpis?.retornosDia ?? 0)],
    ['OS sem encaminhamento', String(kpis?.osSemEncaminhamento ?? 0)],
    ['Tempo médio geral', dash(tempoMedioGeral)],
    ['Tempo médio deslocamento', dash(tempoMedioDeslocamentoGeral)],
    ['Hora da exportação', dash(horaAtual)]
  ];
  return matrix;
}

function styleResumoSheet(ws, matrix) {
  ws['!cols'] = [{ wch: 30 }, { wch: 42 }];
  if (ws.A1) ws.A1.s = TITLE_STYLE;
  const sectionRows = matrix
    .map((row, idx) => (row[0] === 'Filtros aplicados' || row[0] === 'Indicadores' ? idx : -1))
    .filter((i) => i >= 0);
  sectionRows.forEach((r) => {
    const addr = XLSX.utils.encode_cell({ r, c: 0 });
    if (ws[addr]) ws[addr].s = SECTION_STYLE;
  });
  const metricHeaderRow = matrix.findIndex((row) => row[0] === 'Métrica' && row[1] === 'Valor');
  if (metricHeaderRow >= 0) {
    [0, 1].forEach((c) => {
      const addr = XLSX.utils.encode_cell({ r: metricHeaderRow, c });
      if (ws[addr]) {
        ws[addr].s = {
          font: { bold: true, color: { rgb: 'FF1A2230' } },
          fill: { fgColor: { rgb: 'FFE8E4FF' } },
          border: CELL_BORDER
        };
      }
    });
  }
}

function buildTecnicosMatrix(data) {
  const headers = [
    'ID',
    'Técnico',
    'Status',
    'OS em serviço',
    'OS em deslocamento',
    'OS concluídas',
    'Total roteiro',
    'Progresso (%)',
    'Tempo médio',
    'Produzido',
    'Desloc. médio',
    'Desloc. total',
    'Operação',
    'Retorno',
    'Qtd. atividades'
  ];

  const rows = (data.technicians || []).map((tec) => {
    const displayStatus = tec.displayStatus || getTechnicianDisplayStatus(tec, data.isLive);
    const statusLabel = TECH_STATUS_TYPES[displayStatus]?.label || displayStatus;
    const activities = tec.activities || [];
    const progressPct = tec.totalOs > 0 ? Math.round((tec.osCount / tec.totalOs) * 100) : 0;

    return [
      dash(tec.id),
      dash(tec.nome),
      statusLabel,
      formatOsList(tec.osEmServico, 'tempoEmServico'),
      formatOsList(tec.osEmDeslocamento, 'tempoEmDeslocamento'),
      tec.osCount ?? 0,
      tec.totalOs ?? 0,
      progressPct,
      dash(tec.sla),
      dash(tec.osCount > 0 ? tec.tempoProduzido : null),
      dash(tec.tempoMedioDeslocamento),
      dash(tec.tempoTotalDeslocamento),
      activities.some((a) => isFilled(a.condOperacao)) ? 'Sim' : 'Não',
      activities.some((a) => isFilled(a.condRetorno)) ? 'Sim' : 'Não',
      activities.length
    ];
  });

  return [headers, ...rows];
}

function buildTimelineMatrix(data) {
  const headers = [
    'ID técnico',
    'Técnico',
    'Tipo',
    'OS',
    'Início',
    'Fim',
    'Duração',
    'Cliente',
    'Cond. operação',
    'Cond. retorno'
  ];

  const rows = [];
  (data.technicians || []).forEach((tec) => {
    (tec.activities || []).forEach((act) => {
      let inicio = '—';
      let fim = '—';
      let duracao = '—';

      if (act.type === 'deslocamento_trajeto' && act.inicioDeslocamento && act.fimDeslocamento) {
        inicio = formatTimeFromIso(act.inicioDeslocamento);
        fim = formatTimeFromIso(act.fimDeslocamento);
        const start = new Date(act.inicioDeslocamento);
        const end = new Date(act.fimDeslocamento);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
          duracao = formatMinutesAsHhMm((end - start) / 60000);
        }
      } else if (act.type === 'almoco' && act.inicioAlmoco) {
        inicio = formatLunchTimeFromIso(act.inicioAlmoco);
        fim = act.fimAlmoco ? formatLunchTimeFromIso(act.fimAlmoco) : (act.open ? 'agora' : '—');
        if (act.fimAlmoco) {
          const start = new Date(act.inicioAlmoco);
          const end = new Date(act.fimAlmoco);
          if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
            duracao = formatMinutesAsHhMm((end - start) / 60000);
          }
        } else if (act.start != null && act.end != null) {
          duracao = formatDurationFromMin(act.start, act.end);
        }
      } else if (act.start != null && act.end != null) {
        inicio = timelineMinToClock(act.start);
        fim = timelineMinToClock(act.end);
        duracao = formatDurationFromMin(act.start, act.end);
      }

      rows.push([
        dash(tec.id),
        dash(tec.nome),
        formatActivityType(act.type),
        dash(act.label),
        inicio,
        fim,
        duracao,
        dash(act.cliente),
        dash(act.condOperacao),
        dash(act.condRetorno)
      ]);
    });
  });

  return [headers, ...rows];
}

/**
 * Exporta planilha XLSX com resumo, técnicos e timeline.
 * @param {object} exportData
 */
export function exportMonitoramentoXlsx(exportData) {
  if (!exportData?.technicians?.length) {
    throw new Error('Sem dados para exportar.');
  }

  const wb = XLSX.utils.book_new();

  const resumoMatrix = buildResumoMatrix(exportData);
  const wsResumo = XLSX.utils.aoa_to_sheet(resumoMatrix);
  styleResumoSheet(wsResumo, resumoMatrix);
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo');

  const tecMatrix = buildTecnicosMatrix(exportData);
  const wsTec = XLSX.utils.aoa_to_sheet(tecMatrix);
  const tecCols = tecMatrix[0].length;
  applyHeaderRow(wsTec, 0, tecCols);
  if (tecMatrix.length > 1) {
    applyDataStyles(wsTec, 1, tecMatrix.length - 1, tecCols);
  }
  wsTec['!cols'] = [
    { wch: 10 },
    { wch: 28 },
    { wch: 14 },
    { wch: 22 },
    { wch: 22 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 11 },
    { wch: 11 },
    { wch: 12 },
    { wch: 12 },
    { wch: 10 },
    { wch: 10 },
    { wch: 12 }
  ];
  wsTec['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
  XLSX.utils.book_append_sheet(wb, wsTec, 'Técnicos');

  const tlMatrix = buildTimelineMatrix(exportData);
  if (tlMatrix.length > 1) {
    const wsTl = XLSX.utils.aoa_to_sheet(tlMatrix);
    const tlCols = tlMatrix[0].length;
    applyHeaderRow(wsTl, 0, tlCols);
    applyDataStyles(wsTl, 1, tlMatrix.length - 1, tlCols);
    wsTl['!cols'] = [
      { wch: 10 },
      { wch: 26 },
      { wch: 16 },
      { wch: 12 },
      { wch: 9 },
      { wch: 9 },
      { wch: 9 },
      { wch: 30 },
      { wch: 14 },
      { wch: 14 }
    ];
    wsTl['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
    XLSX.utils.book_append_sheet(wb, wsTl, 'Timeline');
  }

  XLSX.writeFile(wb, buildFilename(), { cellStyles: true });
}
